import React, { useState, useEffect } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem } from '@mui/material';
import FormSidebar from './FormSidebar';
import FormDetails from './FormDetails';
import AddFieldDialog from './AddFieldDialog';
import FormsAPI from './FormsAPI';

// Categories (themes + property types) used to render here as a stacked
// sub-section below the forms editor (decision 4.1, /plan-design-review
// 2026-05-13). That layout made the page feel like two unrelated tools
// glued together with a divider; in 2026-06-08 we promoted it back to a
// standalone admin nav item ("Themes & Types") so each surface has a
// single, coherent purpose. See <AdminTaxonomies /> in AdminDashboard.

const FormManager = ({ isGlobalAdmin, userProjectIds }) => {
    const [forms, setForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isAddFieldDialogOpen, setIsAddFieldDialogOpen] = useState(false);
    const [availableFields, setAvailableFields] = useState([]);
    const [projects, setProjects] = useState([]); // List of projects
    const [selectedProject, setSelectedProject] = useState('');
    const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
    const [editSnapshot, setEditSnapshot] = useState(null);

    useEffect(() => {
        FormsAPI.getForms().then((response) => setForms(response.data.forms));
        FormsAPI.getProjects().then((response) => setProjects(response.data.projects));
    }, []);

    const filteredProjects = isGlobalAdmin
        ? projects
        : projects.filter(p => userProjectIds.includes(p.projectid));

    const allowedProjectsids = new Set(filteredProjects.map(p => p.projectsid));

    const filteredForms = isGlobalAdmin
        ? forms
        : forms.filter(f => allowedProjectsids.has(f.projectsid));

    // Fetch fields for the selected form's project
    useEffect(() => {
        if (selectedForm?.projectsid) {
            FormsAPI.getFields(selectedForm.projectsid).then((response) => setAvailableFields(response.data.fields));
        }
    }, [selectedForm]);
    const handleEdit = () => {
        setEditSnapshot(JSON.parse(JSON.stringify(selectedForm)));
        setIsEditing(true);
    };

    const handleCancel = () => {
        if (editSnapshot) setSelectedForm(editSnapshot);
        setIsEditing(false);
        setEditSnapshot(null);
    };


    const handleAddForm = () => {
        setIsProjectDialogOpen(true); // Open project selection dialog
    };

    const handleProjectSelect = () => {
        const selectedProjectData = projects.find((project) => project.projectsid === selectedProject);

        setSelectedForm({
            form_title: '',
            projectsid: selectedProjectData.projectsid,
            project_name: selectedProjectData.project_name, // Set project name
            fields: [],
        });
        setIsEditing(true);
        setIsProjectDialogOpen(false); // Close the project selection dialog
    };


    const handleSaveForm = async (form) => {
        try {
            // 1) Persist any field name/type edits (Model A).
            // BUG-001 part 3 (client): skip protected fields entirely. The
            // SortableField renders them disabled so their values match the
            // DB, but firing a no-op updateField for every protected field on
            // every save is just noise — and if anything in the round-trip
            // canonicalizes whitespace differently from FormHandler's
            // strtolower+trim check we'd risk a spurious 403 here on a
            // submission the admin didn't actually edit.
            await Promise.all(
                (form.fields || [])
                    .filter((f) => !f.protected)
                    .map((f) =>
                        FormsAPI.updateField({
                            fieldId: f.field_id,
                            fieldName: f.field_name,
                            fieldType: f.field_type,
                        })
                    )
            );

            // 2) Save form title + field order
            const fieldIds = (form.fields || []).map((field) => field.field_id);
            const formData = {
                formId: form.id,
                formTitle: form.form_title,
                projectsId: form.projectsid,
                fieldIds: fieldIds,
                formNotes: form.form_notes || '',
            };

            await FormsAPI.saveForm(formData);

            // 3) Refresh
            const formsRes = await FormsAPI.getForms();
            setForms(formsRes.data.forms);

            if (form?.projectsid) {
                const fieldsRes = await FormsAPI.getFields(form.projectsid);
                setAvailableFields(fieldsRes.data.fields);
            }

            setIsEditing(false);
            setEditSnapshot(null);
        } catch (err) {
            console.error(err);
            // BUG-001 part 3 (client): surface FormHandler.php's structured
            // protected-field error (403 with `protected: true` and a human
            // message) instead of the generic "Save failed".
            //
            // Two error shapes to handle since the auth-hardening migration
            // (2026-06-08) moved FormsAPI from axios to apiCall:
            //   - axios:   err.response.data.{error,protected}
            //   - apiCall: err.body.{error,protected} (ApiError)
            // We check both so a partial revert or future re-migration
            // doesn't silently drop the structured error UX.
            const body = err?.response?.data || err?.body || null;
            const serverError = body?.error || err?.message;
            const isProtectedError = body?.protected === true;
            if (serverError) {
                alert(
                    isProtectedError
                        ? `${serverError}\n\nReload the form to pick up the latest required-field list.`
                        : `Save failed: ${serverError}`
                );
            } else {
                alert('Save failed. Check console for details.');
            }
        }
    };


    const handleSelectField = (field) => {
        setSelectedForm((prevForm) => ({
            ...prevForm,
            fields: [...prevForm.fields, field],
        }));
        setIsAddFieldDialogOpen(false);
    };

    const handleCreateNewField = async (fieldName, fieldType) => {
        if (!selectedForm || !selectedForm.projectsid) {
            alert('Please select a project before creating a new field.');
            return;
        }

        try {
            const payload = {
                projectsId: selectedForm.projectsid,
                fieldType: fieldType,
                fieldName: fieldName,
            };

            const res = await FormsAPI.createField(payload);

            const createdField = res.data.field; // from updated PHP

            // Refresh available fields
            const fieldsRes = await FormsAPI.getFields(selectedForm.projectsid);
            setAvailableFields(fieldsRes.data.fields);

            // Add created field to the current form template
            setSelectedForm((prevForm) => ({
                ...prevForm,
                fields: [...(prevForm.fields || []), createdField],
            }));
        } catch (err) {
            console.error(err);
            alert('Create field failed. Check console for details.');
        }
    };



    const handleUpdateForm = (updatedForm) => {
        setSelectedForm(updatedForm);
    };

    return (
        <Box display="flex" sx={{ width: '100%', minHeight: '100%' }}>
            <FormSidebar forms={filteredForms} onSelectForm={setSelectedForm} onAddForm={handleAddForm} />
            {selectedForm && (
                <FormDetails
                    form={selectedForm}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    onSave={() => handleSaveForm(selectedForm)}
                    onEdit={handleEdit}
                    onCancel={handleCancel}
                    onUpdateForm={handleUpdateForm}
                    onCreateField={handleCreateNewField}
                    availableFields={availableFields}
                />
            )}

            <AddFieldDialog
                open={isAddFieldDialogOpen}
                onClose={() => setIsAddFieldDialogOpen(false)}
                existingFields={availableFields}
                onSelectField={handleSelectField}
                onCreateNewField={handleCreateNewField}
            />

            {/* Project Selection Dialog */}
            <Dialog open={isProjectDialogOpen} onClose={() => setIsProjectDialogOpen(false)}>
                <DialogTitle>Select a Project</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value)}
                    >
                        {filteredProjects.map((project) => (
                            <MenuItem key={project.projectsid} value={project.projectsid}>
                                {project.project_name}
                            </MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsProjectDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleProjectSelect} color="primary">Select</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FormManager;
